import React, { useState, Suspense, useEffect, useCallback } from 'react'
import { Box, CircularProgress, Collapse } from '@mui/material'
import EngagementSelector from './EngagementSelector'
import { BreathingGame, MemoryGame, BubblePop } from './games'
import { isTokenInvalidOrAbsent } from 'src/views/pages/login/ProtectedRoute'
import { ClubbedAuthModal } from 'src/consumer/components/SignIn/ClubbedAuthComponents'
import { isValidEmail } from 'src/clinic/views/onboardng/clinicBrand/components/utils'

// Lazy load music player
const SoothingMusicPlayer = React.lazy(() => import('./music/SoothingMusicPlayer'))

// Map activity IDs to components
const GAME_COMPONENTS = {
  breathing: BreathingGame,
  memory: MemoryGame,
  bubbles: BubblePop,
}

// Loading fallback for lazy-loaded components
const LoadingFallback = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '200px',
    }}
  >
    <CircularProgress size={32} sx={{ color: '#8b5cf6' }} />
  </Box>
)

/**
 * WaitingEngagement - Shows games and music while waiting for session
 * 
 * @param {number} waitTimeMs - Time remaining in milliseconds
 * @param {object} engagementConfig - Optional config from API (for music tracks, enabled activities)
 * @param {number} minWaitThreshold - Minimum wait time to show engagement (default: 2 mins)
 * @param {string} sessionSlug - Public session slug (for /v/* routes)
 * @param {string} bookedEventId - Booked event ID (for /l/* routes)
 * @param {string} shortKey - Optional session key/OTP
 * @param {string} clientUserLoginHint - Masked login hint (e.g., "j***@example.com" or "***5678") for music access
 * @param {string} clientUserLoginMethod - Login method type: 'email', 'mobile', 'google', 'facebook', or 'apple'
 */
export default function WaitingEngagement({ 
  waitTimeMs,
  engagementConfig = {},
  minWaitThreshold = 120000, // 2 minutes default
  sessionSlug,
  bookedEventId,
  shortKey,
  clientUserLoginHint = null,
  clientUserLoginMethod = null,
}) {
  const [selectedActivity, setSelectedActivity] = useState(null)
  const [showLoginModal, setShowLoginModal] = useState(false)
  const [pendingMusicSelection, setPendingMusicSelection] = useState(false)

  // Check if user is logged in
  const isLoggedIn = useCallback(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null
    return !isTokenInvalidOrAbsent(token)
  }, [])

  // Handle activity selection with login check for music
  const handleActivitySelect = (activityId) => {
    if (activityId === 'music' && !isLoggedIn()) {
      // Show login modal for music access
      setPendingMusicSelection(true)
      setShowLoginModal(true)
      return
    }
    // For other activities or if logged in, proceed normally
    setSelectedActivity(selectedActivity === activityId ? null : activityId)
  }

  // Check if user logged in and handle music access
  const checkAndHandleLogin = useCallback(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('token') : null
    if (!isTokenInvalidOrAbsent(token) && pendingMusicSelection) {
      // User logged in, now allow music access
      setSelectedActivity('music')
      setPendingMusicSelection(false)
      setShowLoginModal(false)
      return true
    }
    return false
  }, [pendingMusicSelection])

  // Listen for login success (token added to localStorage)
  useEffect(() => {
    const handleStorageChange = () => {
      checkAndHandleLogin()
    }

    // Listen for storage events (when token is added in other tabs/windows)
    window.addEventListener('storage', handleStorageChange)
    
    // Also listen for custom login event that might be dispatched
    const handleLoginSuccess = () => {
      checkAndHandleLogin()
    }
    window.addEventListener('userLoggedIn', handleLoginSuccess)

    // Poll for login status while modal is open (for same-window login)
    let pollInterval = null
    if (showLoginModal && pendingMusicSelection) {
      pollInterval = setInterval(() => {
        if (checkAndHandleLogin()) {
          clearInterval(pollInterval)
        }
      }, 500) // Check every 500ms
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange)
      window.removeEventListener('userLoggedIn', handleLoginSuccess)
      if (pollInterval) {
        clearInterval(pollInterval)
      }
    }
  }, [showLoginModal, pendingMusicSelection, checkAndHandleLogin])

  // Only show if wait time is above threshold
  if (waitTimeMs < minWaitThreshold) {
    return null
  }

  // Get enabled activities from config or use defaults
  const enabledActivities = engagementConfig?.games?.available || 
    ['music', 'breathing', 'memory', 'bubbles']

  // Get music tracks from config
  const musicTracks = engagementConfig?.music?.tracks || [
    { id: 'default-1', name: 'Calm Waters', mediaId: null },
    { id: 'default-2', name: 'Forest Breeze', mediaId: null },
    { id: 'default-3', name: 'Mountain Peace', mediaId: null },
  ]

  const GameComponent = selectedActivity && selectedActivity !== 'music' 
    ? GAME_COMPONENTS[selectedActivity] 
    : null

  return (
    <Box
      sx={{
        width: '100%',
        margin: '0 auto',
        height: '100%',
      }}
    >
      {/* Activity Selector */}
      <Box
        sx={{
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(12px)',
          borderRadius: { xs: '12px', sm: '16px', lg: '20px' },
          padding: { xs: '12px', sm: '16px', lg: '24px' },
          border: '1px solid rgba(139, 92, 246, 0.15)',
          boxShadow: '0 8px 32px rgba(139, 92, 246, 0.1)',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <EngagementSelector
          selectedActivity={selectedActivity}
          onSelect={handleActivitySelect}
          enabledActivities={enabledActivities}
        />

        {/* Activity content area */}
        <Collapse in={selectedActivity !== null}>
          <Box
            sx={{
              marginTop: 2,
              paddingTop: 2,
              borderTop: '1px solid #e5e7eb',
            }}
          >
            <Suspense fallback={<LoadingFallback />}>
              {selectedActivity === 'music' && (
                <SoothingMusicPlayer 
                  tracks={musicTracks}
                  sessionSlug={sessionSlug}
                  bookedEventId={bookedEventId}
                  shortKey={shortKey}
                />
              )}
              {GameComponent && <GameComponent />}
            </Suspense>
          </Box>
        </Collapse>
      </Box>

      {/* Login Modal for Music Access */}
      <ClubbedAuthModal
        open={showLoginModal}
        onClose={() => {
          // Check if user logged in before closing
          if (checkAndHandleLogin()) {
            // User logged in, modal will close and music will show
            return
          }
          // User closed without logging in
          setShowLoginModal(false)
          setPendingMusicSelection(false)
        }}
        loginVariant={true}
        loginFieldHeading={
          clientUserLoginHint && clientUserLoginMethod
            ? `Please login to access music. This session is reserved for: ${clientUserLoginHint}`
            : 'Please login to access music'
        }
        preferredLoginInputType={
          clientUserLoginMethod === 'email' || clientUserLoginMethod === 'mobile'
            ? clientUserLoginMethod
            : clientUserLoginHint 
              ? (clientUserLoginHint.includes('@') ? 'email' : 'mobile')
              : undefined
        }
        preferredLoginInputValue={
          // Only pre-fill for email/mobile, not for social logins
          (clientUserLoginMethod === 'email' || clientUserLoginMethod === 'mobile') && clientUserLoginHint
            ? (clientUserLoginMethod === 'mobile' && clientUserLoginHint.startsWith('+')
                ? clientUserLoginHint.slice(1)
                : clientUserLoginHint)
            : undefined
        }
      />
    </Box>
  )
}
